import React, { useState, useEffect } from 'react';
import { string, bool } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import css from './SocialProofBanner.module.css';

/**
 * SocialProofBanner — shows marketplace trust stats.
 *
 * Fetches live counts from /api/social-proof/stats and displays
 * them as social proof counters (listings, users, transactions).
 *
 * Usage (in LandingPage or Footer):
 *   <SocialProofBanner />
 *
 * @param {string}  [props.className]
 * @param {boolean} [props.compact] — smaller single-line variant
 */
const SocialProofBanner = ({ className, compact }) => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetch('/api/social-proof/stats')
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  if (!stats) return null;

  const fmt = n => {
    if (n >= 1000) return `${Math.floor(n / 100) / 10}k+`;
    return String(n);
  };

  const items = [
    { key: 'listings', value: fmt(stats.listings), labelId: 'SocialProofBanner.listings' },
    { key: 'users', value: fmt(stats.users), labelId: 'SocialProofBanner.users' },
    { key: 'transactions', value: fmt(stats.transactions), labelId: 'SocialProofBanner.transactions' },
  ].filter(item => stats[item.key] > 0);

  if (items.length === 0) return null;

  return (
    <div className={classNames(css.root, compact && css.compact, className)}>
      {items.map(({ key, value, labelId }) => (
        <div key={key} className={css.stat}>
          <span className={css.value}>{value}</span>
          <span className={css.label}>
            <FormattedMessage id={labelId} />
          </span>
        </div>
      ))}
    </div>
  );
};

SocialProofBanner.defaultProps = { className: null, compact: false };
SocialProofBanner.propTypes = { className: string, compact: bool };

export default SocialProofBanner;
